<template>
  <b-modal
    :visible="showModal"
    size="lg"
    centered
    scrollable
    footer-class="sales-page-modal-footer"
    @hidden="closeModal"
  >
    <template #modal-header="{ close }">
      <div class="d-flex align-items-start w-100">
        <div class="flex-grow-1 min-w-0">
          <h2 class="mb-1 h3">{{ title }}</h2>
          <p class="mb-0 text-muted">{{ placeName || 'Seu negócio' }}</p>
        </div>
        <button
          type="button"
          class="close ml-2"
          aria-label="Fechar"
          @click="close"
        >
          <span aria-hidden="true">&times;</span>
        </button>
      </div>
    </template>

    <template #modal-footer>
      <div class="d-flex flex-wrap justify-content-center qr-code-actions w-100">
        <button
          type="button"
          class="btn btn-primary"
          :disabled="!salesPageUrl"
          @click="openSalesPage"
        >
          <i class="fas fa-share mr-2"></i>Abrir link
        </button>
        <button
          type="button"
          class="btn btn-primary"
          :disabled="printingPdf || !salesPageUrl"
          @click="downloadQrCodePdf"
        >
          <i class="fas fa-file-pdf mr-2"></i>
          {{ printingPdf ? 'Gerando PDF...' : 'Baixar PDF' }}
        </button>
        <button
          v-if="isMobileDevice"
          type="button"
          class="btn btn-primary"
          :disabled="!salesPageUrl"
          @click="shareSalesPage"
        >
          <i class="fas fa-share-alt mr-2"></i>Compartilhar
        </button>
        <button
          v-else
          type="button"
          class="btn btn-primary"
          :disabled="!salesPageUrl"
          @click="copySalesPageLink"
        >
          <i class="fas fa-copy mr-2"></i>Copiar link
        </button>
      </div>
    </template>

    <div
      class="sales-page-profile"
      :class="{ 'sales-page-profile--logo-left': logoOnLeft }"
    >
      <div class="sales-page-profile__cover"></div>
      <div class="sales-page-profile__card">
        <div v-if="placeLogoConfigured" class="sales-page-profile__logo">
          <img
            v-if="placeLogoUrl"
            :src="placeLogoUrl"
            :alt="placeName || 'Logo'"
          />
        </div>
        <h3 class="sales-page-profile__name">{{ placeName || 'Seu negócio' }}</h3>
        <p class="sales-page-profile__text">
          {{ description }}
        </p>

        <div id="sales-page-qr" class="sales-page-profile__qr">
          <qrcode-vue
            v-if="salesPageUrl"
            render-as="svg"
            :size="qrSize"
            :value="salesPageUrl"
          />
        </div>

      </div>
    </div>

    <div class="qr-print-source" aria-hidden="true">
      <qrcode-vue
        v-if="salesPageUrl"
        ref="printQr"
        render-as="canvas"
        :size="600"
        :value="salesPageUrl"
      />
    </div>
  </b-modal>
</template>

<script>
import qrcodeVue from 'qrcode.vue'
import { jsPDF } from 'jspdf'
import { mapGetters, mapActions } from 'vuex'
import { loadPublicLogoUrl } from '@/store/utils/publicLogoFile'
import { isMobile } from '@/store/utils/general'

export default {
  name: 'PublicBookingsPageModal',

  components: {
    qrcodeVue,
  },

  props: {
    showModal: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: 'Página de vendas',
    },
    description: {
      type: String,
      default:
        'É a página na qual seu cliente poderá saber mais sobre os passeios e fazer uma cotação online.',
    },
    linkSuffix: {
      type: String,
      default: '',
    },
    logoAlign: {
      type: String,
      default: 'center',
    },
  },

  data() {
    return {
      placeLogoUrl: null,
      placeLogoObjectUrl: null,
      placeLogoConfigured: false,
      printingPdf: false,
    }
  },

  computed: {
    ...mapGetters('auth', {
      place: 'getPlace',
    }),

    ...mapGetters('booking', {
      place_public_data: 'getPlacePublicData',
    }),

    placeName() {
      let name = (this.place && (this.place.name || this.place.place_name)) || ''
      if (name) {
        name = name.replace(/LTDA/g, '').trim()
      }
      return name
    },

    placeUuid() {
      return (this.place && (this.place.uuid || this.place.janooUserUuid)) || ''
    },

    salesPageUrl() {
      if (!this.placeUuid) return ''
      return `${window.location.origin}/public-bookings/${this.placeUuid}${this.linkSuffix || ''}`
    },

    qrSize() {
      if (typeof window === 'undefined') return 180
      return window.innerWidth < 576 ? 160 : 180
    },

    isMobileDevice() {
      return isMobile()
    },

    logoOnLeft() {
      return this.logoAlign === 'left'
    },
  },

  watch: {
    showModal(visible) {
      if (visible) {
        this.ensurePlaceLogo()
      }
    },
    place_public_data: {
      deep: true,
      handler() {
        this.loadPlaceLogo()
      },
    },
  },

  methods: {
    ...mapActions('booking', ['fetchPlacePublicData']),

    async ensurePlaceLogo() {
      if (this.placeUuid) {
        await this.fetchPlacePublicData(this.placeUuid)
      }
      await this.loadPlaceLogo()
    },

    clearPlaceLogoObjectUrl() {
      if (this.placeLogoObjectUrl) {
        URL.revokeObjectURL(this.placeLogoObjectUrl)
        this.placeLogoObjectUrl = null
      }
    },

    async loadPlaceLogo() {
      this.clearPlaceLogoObjectUrl()
      this.placeLogoUrl = null
      this.placeLogoConfigured = false

      try {
        const loaded = await loadPublicLogoUrl(this.place_public_data)
        this.placeLogoConfigured = !!loaded.configured
        if (!loaded.url) return
        if (loaded.owned) this.placeLogoObjectUrl = loaded.url
        this.placeLogoUrl = loaded.url
      } catch (error) {
        console.warn('Não foi possível carregar o logotipo do gestor:', error)
      }
    },

    closeModal() {
      this.clearPlaceLogoObjectUrl()
      this.$emit('close')
    },

    openSalesPage() {
      if (!this.salesPageUrl) return
      window.open(this.salesPageUrl, '_blank', 'noopener')
    },

    notifyLinkCopied() {
      this.$notify({
        message: 'Link copiado!',
        duration: 5000,
        type: 'default',
      })
    },

    notifyLinkCopyFailed() {
      this.$notify({
        message: 'Erro ao copiar o link para área de transferência',
        duration: 6000,
        type: 'error',
      })
    },

    copySalesPageLinkWithFallback() {
      const input = document.createElement('textarea')
      input.value = this.salesPageUrl
      input.setAttribute('readonly', '')
      input.style.position = 'fixed'
      input.style.top = '0'
      input.style.left = '0'
      input.style.opacity = '0'
      document.body.appendChild(input)
      input.focus()
      input.select()
      let copied = false
      try {
        copied = document.execCommand('copy')
      } catch (error) {
        copied = false
      }
      document.body.removeChild(input)
      return copied
    },

    copySalesPageLink() {
      if (!this.salesPageUrl) return
      if (this.copySalesPageLinkWithFallback()) {
        this.notifyLinkCopied()
        return
      }
      if (!navigator.clipboard || !navigator.clipboard.writeText) {
        this.notifyLinkCopyFailed()
        return
      }

      navigator.clipboard.writeText(this.salesPageUrl).then(
        () => this.notifyLinkCopied(),
        () => this.notifyLinkCopyFailed()
      )
    },

    async shareSalesPage() {
      if (!this.salesPageUrl) return
      if (!navigator.share) {
        this.copySalesPageLink()
        return
      }

      try {
        await navigator.share({
          title: this.placeName || this.title,
          url: this.salesPageUrl,
        })
      } catch (error) {
        if (error && error.name === 'AbortError') return
        this.$notify({
          message: 'Erro ao tentar compartilhar',
          duration: 6000,
          type: 'error',
        })
      }
    },

    async downloadQrCodePdf() {
      if (this.printingPdf || !this.salesPageUrl) return
      this.printingPdf = true
      const filename = `${this.title} - ${this.placeName || 'negócio'}.pdf`
      try {
        await this.$nextTick()
        const doc = new jsPDF({
          unit: 'mm',
          format: 'a4',
          orientation: 'portrait',
        })
        const pageW = 210
        const pageH = 297
        const margin = 18
        const contentW = pageW - margin * 2
        const navy = [18, 87, 110]
        const muted = [82, 95, 127]
        const coverH = 68

        doc.setFillColor(23, 43, 77)
        doc.rect(0, 0, pageW, coverH, 'F')
        doc.setFillColor(...navy)
        doc.rect(0, coverH - 18, pageW, 18, 'F')

        doc.setFillColor(255, 255, 255)
        doc.roundedRect(14, 50, pageW - 28, pageH - 68, 4, 4, 'F')

        const logoData = await this.circularLogoDataUrl()
        const logoSize = 32
        const logoY = 34
        const logoX = this.logoOnLeft ? margin : (pageW - logoSize) / 2
        const textAlign = this.logoOnLeft ? 'left' : 'center'
        doc.setFillColor(255, 255, 255)
        doc.circle(logoX + logoSize / 2, logoY + logoSize / 2, logoSize / 2 + 2, 'F')
        if (logoData) {
          doc.addImage(logoData, 'PNG', logoX, logoY, logoSize, logoSize)
        }

        let y = logoY + logoSize + 12
        doc.setTextColor(...navy)
        doc.setFont('helvetica', 'bold')
        doc.setFontSize(20)
        y = this.writeCentered(
          doc,
          this.placeName || 'Seu negócio',
          y,
          contentW,
          margin,
          8,
          textAlign
        )
        y += 2

        doc.setFont('helvetica', 'normal')
        doc.setFontSize(12)
        doc.setTextColor(...muted)
        y = this.writeCentered(
          doc,
          this.title,
          y,
          contentW,
          margin,
          6,
          textAlign
        )
        y += 8

        doc.setFontSize(13)
        doc.setTextColor(50, 50, 93)
        y = this.writeCentered(
          doc,
          this.description,
          y,
          this.logoOnLeft ? contentW : contentW - 16,
          this.logoOnLeft ? margin : margin + 8,
          7,
          textAlign
        )
        y += 10

        const printRoot = this.$refs.printQr && this.$refs.printQr.$el
        const qrCanvas =
          printRoot &&
          (printRoot.tagName === 'CANVAS'
            ? printRoot
            : printRoot.querySelector('canvas'))
        if (qrCanvas && qrCanvas.toDataURL) {
          const qrSize = 78
          doc.addImage(
            qrCanvas.toDataURL('image/png'),
            'PNG',
            (pageW - qrSize) / 2,
            y,
            qrSize,
            qrSize
          )
          y += qrSize + 14
        }

        const steps = [
          'Aponte a câmera do celular para o código.',
          'Veja os passeios e experiências disponíveis.',
          'Peça uma cotação online.',
        ]
        steps.forEach((step, index) => {
          const circleX = margin + 5
          doc.setFillColor(...navy)
          doc.circle(circleX, y + 1.5, 4, 'F')
          doc.setTextColor(255, 255, 255)
          doc.setFont('helvetica', 'bold')
          doc.setFontSize(11)
          doc.text(String(index + 1), circleX, y + 2.8, { align: 'center' })

          doc.setTextColor(31, 42, 46)
          doc.setFont('helvetica', 'normal')
          doc.setFontSize(12)
          const lines = doc.splitTextToSize(step, contentW - 16)
          doc.text(lines, margin + 14, y + 2.5)
          y += Math.max(12, lines.length * 6 + 4)
        })

        doc.save(filename)
      } catch (error) {
        this.$notify({
          message: 'Não foi possível gerar o PDF',
          duration: 6000,
          type: 'error',
        })
      } finally {
        this.printingPdf = false
      }
    },

    writeCentered(doc, text, y, width, x, lineHeight, align) {
      const lines = doc.splitTextToSize(String(text || ''), width)
      const mode = align === 'left' ? 'left' : 'center'
      const anchor = mode === 'left' ? x : x + width / 2
      doc.text(lines, anchor, y, { align: mode })
      return y + lines.length * lineHeight
    },

    circularLogoDataUrl() {
      const src = this.placeLogoUrl
      if (!src) return Promise.resolve('')
      return new Promise((resolve) => {
        const image = new Image()
        image.crossOrigin = 'anonymous'
        image.onload = () => {
          try {
            const size = 256
            const canvas = document.createElement('canvas')
            canvas.width = size
            canvas.height = size
            const ctx = canvas.getContext('2d')
            ctx.fillStyle = '#ffffff'
            ctx.beginPath()
            ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2)
            ctx.closePath()
            ctx.fill()
            ctx.save()
            ctx.beginPath()
            ctx.arc(size / 2, size / 2, size / 2 - 6, 0, Math.PI * 2)
            ctx.closePath()
            ctx.clip()
            const pad = 18
            ctx.drawImage(image, pad, pad, size - pad * 2, size - pad * 2)
            ctx.restore()
            resolve(canvas.toDataURL('image/png'))
          } catch (error) {
            resolve('')
          }
        }
        image.onerror = () => resolve('')
        image.src = src
      })
    },
  },

  mounted() {
    if (this.showModal) {
      this.ensurePlaceLogo()
    }
  },

  beforeDestroy() {
    this.clearPlaceLogoObjectUrl()
  },
}
</script>

<style>
.sales-page-profile {
  position: relative;
  margin: -1rem -1rem 0;
}

.sales-page-profile__cover {
  height: 110px;
  background-image: linear-gradient(
    87deg,
    #172b4d 0,
    #12576e 100%
  );
}

.sales-page-profile__card {
  position: relative;
  margin: -4.5rem 1rem 0;
  padding: 0 1.25rem 1.5rem;
  background: #fff;
  border-radius: 0.5rem;
  box-shadow: 0 0 2rem 0 rgba(136, 152, 170, 0.15);
  text-align: center;
}

.sales-page-profile__logo {
  width: 7rem;
  height: 7rem;
  margin: -3.5rem auto 1rem;
  border-radius: 50%;
  overflow: hidden;
  border: 4px solid #fff;
  background: #fff;
  box-shadow: 0 4px 12px rgba(20, 103, 130, 0.12);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.75rem;
}

.sales-page-profile--logo-left .sales-page-profile__card {
  text-align: left;
}

.sales-page-profile--logo-left .sales-page-profile__logo {
  margin-left: 0;
  margin-right: 0;
}

.sales-page-profile--logo-left .sales-page-profile__text {
  margin-left: 0;
  margin-right: 0;
}

.sales-page-profile--logo-left .sales-page-profile__qr {
  display: block;
  text-align: center;
}

.sales-page-profile__logo img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.sales-page-profile__name {
  margin: 0 0 0.15rem;
  color: #12576e;
  font-size: 1.35rem;
  font-weight: 700;
}

.sales-page-profile__role {
  margin: 0 0 0.85rem;
  color: #8898aa;
  font-size: 0.9rem;
  font-weight: 500;
}

.sales-page-profile__text {
  margin: 0 auto 1.25rem;
  max-width: 32rem;
  color: #525f7f;
  font-size: 0.95rem;
  line-height: 1.5;
}

.sales-page-profile__qr {
  display: inline-block;
  margin-bottom: 0.25rem;
}

.sales-page-modal-footer {
  justify-content: center;
}

.qr-code-actions {
  gap: 0.5rem;
}

.qr-code-actions .btn {
  margin: 0.15rem;
}

.qr-print-source {
  position: absolute;
  width: 0;
  height: 0;
  overflow: hidden;
}
</style>
